/**
 * Shared logic for building the template list from feature metrics API (Level 1, Level 2, bulk-manage).
 * Used by both Data Scenarios sidebar and WYSIWYG Templates sidebar so they display the same list.
 */
import { Channel, FeatureResolution, TemplateSummary } from 'types/cx-editor';
import { SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';
import { expandTemplates } from 'utils/template-expansion-utils';

export const VALID_FUEL_TYPES = ['ELECTRIC', 'GAS', 'WATER', 'DUAL', 'DUAL_FUEL', 'DUALFUEL'];

export interface SectionItem {
  sectionKey: string;
  sectionId: string;
  name: string;
  position?: number | null;
  featureResolution?: FeatureResolution;
}

export interface TemplateWithSections extends TemplateSummary {
  sections: SectionItem[];
  fuelType?: string;
}

export interface ChannelWithTemplates {
  name: Channel;
  templates: TemplateWithSections[];
}

export const isValidFuelType = (fuelType?: string | null): boolean => {
  if (!fuelType) return false;
  return VALID_FUEL_TYPES.includes(fuelType.trim().toUpperCase());
};

export const normalizeFuelType = (fuelType?: string | null): string => {
  if (!fuelType) return 'ELECTRIC';
  const normalized = fuelType.trim().toUpperCase();
  return isValidFuelType(normalized) ? normalized : 'ELECTRIC';
};

export const matchesAnyFuelType = (
  templateFuelType: string | null | undefined,
  allowedFuelTypes: string[]
): boolean => {
  if (!templateFuelType) return true;
  const normalized = normalizeFuelType(templateFuelType);
  return allowedFuelTypes.includes(normalized);
};

export const getFuelTypeLabel = (fuelType?: string | null): string => {
  const n = normalizeFuelType(fuelType);
  switch (n) {
    case 'ELECTRIC':
      return 'EL';
    case 'GAS':
      return 'GAS';
    case 'WATER':
      return 'WATER';
    case 'DUAL':
    case 'DUAL_FUEL':
    case 'DUALFUEL':
      return 'DF';
    default:
      return 'EL';
  }
};

const parseL1BaseKey = (featureId: string): string => {
  const parts = featureId.split('.');
  if (parts.length < 2) return featureId;
  const lastPart = parts[parts.length - 1].toUpperCase();
  if (VALID_FUEL_TYPES.includes(lastPart)) {
    return parts.slice(0, -1).join('.');
  }
  return featureId;
};

export const getSectionDisplayName = (sectionId: string): string => {
  let displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[sectionId];
  if (!displayName) {
    const baseId = sectionId.replace(/_[Vv]\d+$/, '');
    displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[baseId];
  }
  if (!displayName) {
    const cleanId = sectionId.replace(/_[Vv]\d+.*$/, '');
    displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[cleanId];
  }
  if (!displayName) {
    displayName = sectionId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  return displayName;
};

export const extractSmartDisplayName = (featureId: string, displayName?: string | null): string => {
  if (displayName && displayName.length > 2 && displayName.toLowerCase() !== 'df') {
    return displayName;
  }
  const parts = featureId.split('.');
  if (parts.length < 2) return featureId;
  let meaningfulPart = '';
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i].toUpperCase();
    if (VALID_FUEL_TYPES.includes(part) || ['EMAIL', 'WEB', 'HER', 'SMS', 'DF', 'SF'].includes(part)) {
      continue;
    }
    meaningfulPart = parts[i];
    break;
  }
  if (!meaningfulPart) {
    meaningfulPart = parts[parts.length - 1];
  }
  return meaningfulPart
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const convertBulkManageToWebTemplates = (
  bulkManageData: any[],
  projectFuelTypes: string[],
  showAllFuelTypes: boolean
): TemplateWithSections[] => {
  const templateMap = new Map<string, TemplateWithSections>();
  if (!Array.isArray(bulkManageData)) return [];

  const expandedData = expandTemplates(bulkManageData);
  expandedData.forEach(item => {
    const productType = item?.productType;
    if (!productType) return;

    if (!showAllFuelTypes && item?.fuelType) {
      const itemFuelType =
        typeof item.fuelType === 'string'
          ? item.fuelType.trim().toUpperCase()
          : String(item.fuelType).trim().toUpperCase();
      if (isValidFuelType(itemFuelType) && !matchesAnyFuelType(itemFuelType, projectFuelTypes)) {
        return;
      }
    }

    const displayName = item?.displayName || extractSmartDisplayName(productType, item?.displayName);
    const templateKey = productType;
    const rawFuelType = item?.fuelType
      ? typeof item.fuelType === 'string' ? item.fuelType : String(item.fuelType)
      : null;
    const normalizedFuelType = normalizeFuelType(rawFuelType);

    if (!templateMap.has(templateKey)) {
      templateMap.set(templateKey, {
        templateKey,
        name: displayName,
        channel: 'Web' as Channel,
        sections: [],
        fuelType: normalizedFuelType,
      });
    }
  });
  return Array.from(templateMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Transforms Level 1 + Level 2 + bulk-manage API data into channels with templates.
 * Single source of truth for template list used by Data Scenarios and WYSIWYG sidebars.
 */
export function transformToHierarchicalData(
  level1Data: FeatureResolution[],
  level2Data: FeatureResolution[] | null,
  fuelType: string,
  projectFuelTypes: string[],
  showAllFuelTypes: boolean = false,
  bulkManageData: any[] | null = null
): ChannelWithTemplates[] {
  if (!Array.isArray(level1Data)) {
    level1Data = [];
  }

  const channelMap = new Map<Channel, Map<string, TemplateWithSections>>();
  const l1BaseKeyToTemplateKey = new Map<string, string[]>();

  const l1AccessResolutions = level1Data.filter(r => {
    if (!r || r.resolution !== 'ACCESS') return false;
    if (showAllFuelTypes) return true;
    if (r.fuelType) {
      const resFuelType =
        typeof r.fuelType === 'string'
          ? r.fuelType.trim().toUpperCase()
          : String(r.fuelType).trim().toUpperCase();
      if (isValidFuelType(resFuelType)) {
        return matchesAnyFuelType(resFuelType, projectFuelTypes);
      }
    }
    return true;
  });

  l1AccessResolutions.forEach(resolution => {
    if (!resolution || !resolution.featureId) return;
    const productValue = resolution.product;
    if (!productValue || typeof productValue !== 'string') return;

    const product = productValue.trim().toUpperCase();
    let channelName: Channel;
    if (product === 'EMAIL') channelName = 'Email';
    else if (product === 'SMS') channelName = 'SMS';
    else if (product === 'WEB') return;
    else if (product === 'HER') return;
    else return;

    const templateKey = resolution.featureId;
    const baseKey = parseL1BaseKey(templateKey);
    const displayName = extractSmartDisplayName(resolution.featureId, resolution.displayName);
    const rawFuelType = resolution.fuelType
      ? typeof resolution.fuelType === 'string' ? resolution.fuelType : String(resolution.fuelType)
      : null;
    const normalizedFuelType = normalizeFuelType(rawFuelType);

    if (!channelMap.has(channelName)) {
      channelMap.set(channelName, new Map());
    }
    const templateMap = channelMap.get(channelName)!;
    if (!templateMap.has(templateKey)) {
      templateMap.set(templateKey, {
        templateKey,
        name: displayName,
        channel: channelName,
        sections: [],
        fuelType: normalizedFuelType,
      });
      if (!l1BaseKeyToTemplateKey.has(baseKey)) {
        l1BaseKeyToTemplateKey.set(baseKey, []);
      }
      l1BaseKeyToTemplateKey.get(baseKey)!.push(templateKey);
    }
  });

  if (level2Data && Array.isArray(level2Data)) {
    const l2AccessResolutions = level2Data.filter(r => {
      if (!r || r.resolution !== 'ACCESS') return false;
      if (showAllFuelTypes) return true;
      if (r.fuelType) {
        const resFuelType =
          typeof r.fuelType === 'string'
            ? r.fuelType.trim().toUpperCase()
            : String(r.fuelType).trim().toUpperCase();
        if (isValidFuelType(resFuelType)) {
          return matchesAnyFuelType(resFuelType, projectFuelTypes);
        }
      }
      return true;
    });

    l2AccessResolutions.forEach(resolution => {
      const productValue = resolution.product;
      if (!productValue || typeof productValue !== 'string') return;
      const product = productValue.trim().toUpperCase();

      if (product === 'WEB') return;

      let channelName: Channel;
      if (product === 'EMAIL') channelName = 'Email';
      else if (product === 'SMS') channelName = 'SMS';
      else if (product === 'HER') return;
      else return;

      if (!resolution.section) return;

      const sectionKey = resolution.featureId || resolution.section;
      const sectionId = resolution.section;
      const sectionName = getSectionDisplayName(sectionId);
      const section: SectionItem = {
        sectionKey,
        sectionId,
        name: sectionName,
        position: resolution.position,
        featureResolution: resolution,
      };

      if (channelMap.has(channelName) && resolution.productType) {
        const templateMap = channelMap.get(channelName)!;
        let parentTemplate = templateMap.get(resolution.productType);
        if (!parentTemplate) {
          const matchingTemplateKeys = l1BaseKeyToTemplateKey.get(resolution.productType);
          if (matchingTemplateKeys?.length) {
            const l2FuelType = normalizeFuelType(resolution.fuelType);
            for (const key of matchingTemplateKeys) {
              const template = templateMap.get(key);
              if (template && template.fuelType === l2FuelType) {
                parentTemplate = template;
                break;
              }
            }
            if (!parentTemplate) {
              parentTemplate = templateMap.get(matchingTemplateKeys[0]);
            }
          }
        }
        if (parentTemplate && !parentTemplate.sections.find(s => s.sectionKey === sectionKey)) {
          parentTemplate.sections.push(section);
        }
      }
    });
  }

  if (bulkManageData && Array.isArray(bulkManageData) && bulkManageData.length > 0) {
    const webTemplates = convertBulkManageToWebTemplates(
      bulkManageData,
      projectFuelTypes,
      showAllFuelTypes
    );
    if (webTemplates.length > 0) {
      const channelName: Channel = 'Web';
      if (!channelMap.has(channelName)) {
        channelMap.set(channelName, new Map());
      }
      const templateMap = channelMap.get(channelName)!;
      webTemplates.forEach(template => {
        if (!templateMap.has(template.templateKey)) {
          templateMap.set(template.templateKey, template);
        }
      });
    }
  } else if (level2Data && Array.isArray(level2Data)) {
    const webL2AccessResolutions = level2Data.filter(r => {
      if (!r || r.resolution !== 'ACCESS') return false;
      const productValue = r.product;
      if (!productValue || typeof productValue !== 'string') return false;
      if (productValue.trim().toUpperCase() !== 'WEB') return false;
      if (!r.productType) return false;
      const productTypeUpper = r.productType.toUpperCase();
      const fuelTypeUpper = fuelType.toUpperCase();
      const startsWithDashboard = productTypeUpper.startsWith('DASHBOARD.');
      const containsFuelType =
        productTypeUpper.includes('.' + fuelTypeUpper + '.') ||
        productTypeUpper.endsWith('.' + fuelTypeUpper) ||
        productTypeUpper === 'DASHBOARD.' + fuelTypeUpper;
      if (!startsWithDashboard || !containsFuelType) return false;
      if (!r.section) return false;
      if (showAllFuelTypes) return true;
      if (r.fuelType) {
        const resFuelType =
          typeof r.fuelType === 'string'
            ? r.fuelType.trim().toUpperCase()
            : String(r.fuelType).trim().toUpperCase();
        if (isValidFuelType(resFuelType)) {
          return matchesAnyFuelType(resFuelType, projectFuelTypes);
        }
      }
      return true;
    });

    webL2AccessResolutions.forEach(resolution => {
      if (!resolution.featureId) return;
      const channelName: Channel = 'Web';
      const templateKey = resolution.featureId;
      const displayName = resolution.section
        ? getSectionDisplayName(resolution.section)
        : extractSmartDisplayName(resolution.featureId, resolution.displayName);
      const rawFuelType = resolution.fuelType
        ? typeof resolution.fuelType === 'string'
          ? resolution.fuelType
          : String(resolution.fuelType)
        : null;
      const normalizedFuelType = normalizeFuelType(rawFuelType);

      if (!channelMap.has(channelName)) {
        channelMap.set(channelName, new Map());
      }
      const templateMap = channelMap.get(channelName)!;
      if (!templateMap.has(templateKey)) {
        templateMap.set(templateKey, {
          templateKey,
          name: displayName,
          channel: channelName,
          sections: [],
          fuelType: normalizedFuelType,
        });
      }
    });
  }

  const hasHerInLevel1 = l1AccessResolutions.some(r => {
    if (!r || !r.product) return false;
    const product = typeof r.product === 'string' ? r.product.trim().toUpperCase() : '';
    return product === 'HER' && r.resolution === 'ACCESS';
  });

  if (hasHerInLevel1 && level2Data && Array.isArray(level2Data)) {
    const herResolutions = level2Data.filter(r => {
      if (!r || r.resolution !== 'ACCESS') return false;
      const product = r.product?.trim().toUpperCase();
      if (product !== 'HER') return false;
      if (!showAllFuelTypes && r.fuelType) {
        const resFuelType =
          typeof r.fuelType === 'string'
            ? r.fuelType.trim().toUpperCase()
            : String(r.fuelType).trim().toUpperCase();
        if (isValidFuelType(resFuelType)) {
          return matchesAnyFuelType(resFuelType, projectFuelTypes);
        }
      }
      return true;
    });

    const herTemplateMap = new Map<string, TemplateWithSections>();
    herResolutions.forEach(resolution => {
      const featureId = resolution.featureId;
      if (!featureId) return;
      const parts = featureId.split('.');
      if (parts.length < 2) return;

      const templateKey = featureId;
      const fuelTypeFromKey = parts[parts.length - 1]?.toUpperCase() || 'ELECTRIC';
      const normalizedFuelType = normalizeFuelType(fuelTypeFromKey);
      const displayName =
        resolution.displayName || extractSmartDisplayName(featureId, resolution.displayName);

      if (!herTemplateMap.has(templateKey)) {
        herTemplateMap.set(templateKey, {
          templateKey,
          name: displayName,
          channel: 'HER' as Channel,
          sections: [],
          fuelType: normalizedFuelType,
        });
      }

      const template = herTemplateMap.get(templateKey)!;
      const rawData = resolution.featureValueResolutionData;
      if (rawData) {
        try {
          const fallbacks: string[][] =
            typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
          if (Array.isArray(fallbacks)) {
            fallbacks.forEach(fallback => {
              if (Array.isArray(fallback)) {
                fallback.forEach(sectionName => {
                  if (sectionName && typeof sectionName === 'string') {
                    const sectionKey = sectionName;
                    const sectionId = sectionName;
                    const displayName = getSectionDisplayName(sectionId);
                    if (!template.sections.find(s => s.sectionKey === sectionKey)) {
                      template.sections.push({
                        sectionKey,
                        sectionId,
                        name: displayName,
                        position: resolution.position,
                        featureResolution: resolution,
                      });
                    }
                  }
                });
              }
            });
          }
        } catch {
          // ignore parse errors
        }
      }
    });

    if (herTemplateMap.size > 0) {
      const channelName: Channel = 'HER';
      if (!channelMap.has(channelName)) {
        channelMap.set(channelName, new Map());
      }
      const templateMap = channelMap.get(channelName)!;
      herTemplateMap.forEach((template, key) => {
        template.sections.sort((a, b) => {
          if (
            a.position != null &&
            b.position != null &&
            a.position !== undefined &&
            b.position !== undefined
          ) {
            return a.position - b.position;
          }
          return a.name.localeCompare(b.name);
        });
        templateMap.set(key, template);
      });
    }
  }

  const channelOrder: Channel[] = ['Email', 'HER', 'SMS', 'Web'];
  return channelOrder
    .filter(ch => channelMap.has(ch))
    .map(ch => {
      const templateMap = channelMap.get(ch)!;
      const templates = Array.from(templateMap.values())
        .map(t => ({
          ...t,
          sections: t.sections.sort((a, b) => {
            if (
              a.position != null &&
              b.position != null &&
              a.position !== undefined &&
              b.position !== undefined
            ) {
              return a.position - b.position;
            }
            return a.name.localeCompare(b.name);
          }),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      return { name: ch, templates };
    })
    .filter(ch => ch.templates.length > 0);
}
